import { useMemo, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import toast from 'react-hot-toast'
import EventRepeatIcon from '@mui/icons-material/EventRepeat'
import BuildCircleIcon from '@mui/icons-material/BuildCircle'
import AddIcon from '@mui/icons-material/Add'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import { InputRow } from 'Features/SchedulerView/SchedulerView.types'
import {
  WorkstationDownTimeRowType,
  ProductionRequirementRowType,
  SchedulerPackagingRoutesRowType,
} from './Scheduler.types'
import {
  SchedulerRunConfig,
  expandNotProducedKeys,
  lineProductKey,
  settingsToRunConfig,
  useConfigurationSettings,
  usePermissions,
  useScheduleSettings,
} from './Scheduler.data'
import { parsePeriods, joinPeriods, Window } from './RulesWorkbench.timeline'
import { FONT_SANS, TOKENS, PanelHead } from './SchedulerDesign'

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const SIDEBAR_W = 340

const dayTimeToHour = (day: number, hh: number, mm: number): number =>
  day * 24 + hh + mm / 60

const hourToLabel = (hour: number): string => {
  const day = Math.floor(hour / 24) % 7
  const rem = hour - Math.floor(hour / 24) * 24
  const hh = Math.floor(rem)
  const mm = Math.round((rem - hh) * 60)
  return `${DAY_LABELS[day]} ${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`
}

/** "HH:MM" <-> {hh, mm} for the native <input type="time"> value. */
const parseTimeStr = (s: string): { hh: number; mm: number } => {
  const [hh, mm] = s.split(':').map((n) => parseInt(n, 10) || 0)
  return { hh, mm }
}

type PendingDowntime = {
  resource: string
  resourceType: string
  startHour: number
  endHour: number
}

/** What a checklist line represents. The table covers the whole week now,
 *  not only the runs that started before the cut-off, and the four kinds
 *  behave differently enough that the render keys off this rather than
 *  re-deriving it from null checks.
 *
 *  - `ran`       a run that STARTED before the cut-off. Tickable "not
 *                produced", and credited pro-rata for what it made by then.
 *  - `scheduled` a run starting at or after the cut-off. Nothing has been
 *                made, so nothing is credited and there is nothing to tick —
 *                but it is still work the week owes, and its quantity has
 *                always counted towards the product's written remainder. It
 *                was simply invisible, which is half of why the visible rows
 *                never added up to the figure that got saved.
 *  - `demand`    a product the week demands that has no run at all.
 *  - `available` a requirement row carrying NO demand (quantity 0). This is
 *                the bulk of the table — 324 of 350 rows on a real schedule —
 *                and it is what retires "add a product": a product is out of
 *                the week because its row says 0, not because it has no row,
 *                so bringing it in is an edit, not an insert.
 */
type RowKind = 'ran' | 'scheduled' | 'demand' | 'available'

type ChecklistRow = {
  key: string
  kind: RowKind
  productCode: string
  productName: string
  line: string
  kitchen: string
  baseMaterial: string
  /** Absolute horizon hour, or null for a product with no run. */
  startHour: number | null
  /** What this line is meant to make: the run's quantity for a run, the
   *  requirement's demand for a product with none, 0 for an available one. */
  quantity: number
  /** Produced by the cut-off, pro-rata across the line window.
   *
   *  The same arithmetic the engine uses (`mid_week::classify_row`), and it
   *  has to stay the same: this is the number the planner corrects, so a
   *  figure shown here that the engine would not have computed makes every
   *  untouched row wrong. */
  made: number
}

const KIND_RANK: Record<RowKind, number> = {
  ran: 0,
  scheduled: 1,
  demand: 2,
  available: 3,
}

// Runs first in the order they happen, then the week's unscheduled demand,
// then everything merely available. The rows a planner came here to act on
// stay at the top however long the tail of available products grows.
const compareRows = (a: ChecklistRow, b: ChecklistRow): number => {
  const rank = KIND_RANK[a.kind] - KIND_RANK[b.kind]
  if (rank !== 0) return rank
  if (
    a.startHour !== null &&
    b.startHour !== null &&
    a.startHour !== b.startHour
  ) {
    return a.startHour - b.startHour
  }
  return a.productCode.localeCompare(b.productCode)
}

/** Demand on a requirement row. `quantity` is stored as a string. */
const demandOf = (req: ProductionRequirementRowType): number =>
  Number(req.quantity) || 0

/** Rendered beyond this many `available` rows, the list is capped and says so.
 *  Only the available tail is ever capped — a run or a demanded product is
 *  never hidden, whatever the search says. */
const AVAILABLE_ROW_CAP = 150

/** Quantity produced by `cutoff` on a run spanning `[start, end)`.
 *  Mirrors `scheduler::mid_week::classify_row`. */
const madeByCutoff = (
  start: number,
  end: number,
  quantity: number,
  cutoff: number
): number => {
  if (end <= cutoff) return quantity
  const span = end - start
  if (span <= 0) return quantity
  const fraction = Math.min(1, Math.max(0, (cutoff - start) / span))
  return quantity * fraction
}

type MidWeekRescheduleTabProps = {
  schedulerId: string
  iteration: string
  outputRows: InputRow[]
  downtimeRows: WorkstationDownTimeRowType[]
  notProducedIds: Set<string>
  onToggleNotProduced: (key: string) => void
  /** This week's production-requirement rows. Needed to write
   *  `quantityStillToMake` back: the edit is keyed by product code but the
   *  save is keyed by row id, so the row has to be found. */
  requirementRows?: ProductionRequirementRowType[]
  /** Packing routes for this schedule. They decide which products can be
   *  ADDED: the engine drops any requirement whose product has no route
   *  ("has demand but no packing route - excluded"), so offering one would
   *  be offering a row that silently does nothing. */
  packagingRoutes?: SchedulerPackagingRoutesRowType[]
  /** Awaitable, for the same reason as the downtime save below: the run
   *  reads this table fresh from storage, so an un-awaited PUT races it. */
  onSaveRequirementsAsync?: (
    changed: Record<number, ProductionRequirementRowType>
  ) => Promise<unknown>
  /** In-progress edits, owned by the page (`SchedulePackagingRoutes`) rather
   *  than by this panel.
   *
   *  This tab is unmounted by three ordinary actions — switching the
   *  bottom-strip tab, clicking a Factory-layout asset, leaving the Factory
   *  tab — so state held here does not survive a click, and typed quantities
   *  are much too expensive to retype to lose that way. `notProducedIds` is
   *  owned up there already; these travel with it so ticks and figures share
   *  one lifetime instead of two.
   *
   *  Optional, with local state as the fallback, so the panel still works
   *  standalone. */
  stillToMake?: Record<string, string>
  setStillToMake?: React.Dispatch<React.SetStateAction<Record<string, string>>>
  dontStartBefore?: Record<string, string>
  setDontStartBefore?: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >
  finishBefore?: Record<string, string>
  setFinishBefore?: React.Dispatch<React.SetStateAction<Record<string, string>>>
  // Awaitable — must resolve before the run is triggered, since the run
  // reads this same table server-side (see handleLockAndRerun).
  onSaveDowntimeAsync: (
    changed: Record<number, WorkstationDownTimeRowType>
  ) => Promise<unknown>
  // Absolute-horizon offset for the currently displayed week — 0 for a
  // single-week schedule, (week - earliest) * 168 for week >= 2 of a
  // consolidated multi-week run. See SchedulePackagingRoutes.view.tsx's
  // `midWeekBaseHour`. Applies to output-row time fields ONLY — downtime
  // windows are stored per-week-relative already, so they never use this.
  weekBaseHour: number
  runScheduler: (params: {
    schedulerId: string
    iteration: string
    config: SchedulerRunConfig
  }) => void
  isRunning: boolean
}

// Original / made / still-to-make / the two timing windows sit at the right,
// where the eye lands last and where every editable cell in the row belongs.
const CHECKLIST_ROW_COLS =
  '32px minmax(0,1.6fr) 84px minmax(0,1fr) 92px 76px 70px 92px 104px 100px'

// Ten columns do not fit the bottom strip at every width, so the table scrolls
// sideways inside its own box rather than letting the columns crush.
const CHECKLIST_MIN_W = 980

type SortKey =
  | 'ticked'
  | 'product'
  | 'line'
  | 'kitchen'
  | 'start'
  | 'original'
  | 'made'
  | 'still'
  | 'dsb'
  | 'fb'

const CHECKLIST_COLUMNS: { key: SortKey; label: string }[] = [
  { key: 'ticked', label: '✓' },
  { key: 'product', label: 'Product' },
  { key: 'line', label: 'Line' },
  { key: 'kitchen', label: 'Kitchen · material' },
  { key: 'start', label: 'Start' },
  { key: 'original', label: 'Original' },
  { key: 'made', label: 'Made' },
  { key: 'still', label: 'Still to make' },
  { key: 'dsb', label: 'Start no earlier (h)' },
  { key: 'fb', label: 'Finish before (h)' },
]

/** A cell's sort value. `null` means "nothing here" — a product with no run
 *  has no start, a blank timing box is no constraint — and sorts LAST in both
 *  directions, so reversing never fills the top of the table with blanks. */
type SortValue = string | number | null

const compareSortValues = (
  a: SortValue,
  b: SortValue,
  dir: 'asc' | 'desc'
): number => {
  if (a === null && b === null) return 0
  if (a === null) return 1
  if (b === null) return -1
  const cmp =
    typeof a === 'string' || typeof b === 'string'
      ? String(a).localeCompare(String(b))
      : (a as number) - (b as number)
  return dir === 'asc' ? cmp : -cmp
}

const ELLIPSIS = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const

const CELL = {
  fontSize: '0.68rem',
  color: 'text.secondary',
  ...ELLIPSIS,
} as const

const NUM_CELL = {
  fontSize: '0.68rem',
  color: 'text.secondary',
  fontVariantNumeric: 'tabular-nums',
} as const

const NUM_INPUT = {
  width: '100%',
  height: 24,
  px: 0.5,
  fontSize: '0.68rem',
  fontFamily: FONT_SANS,
  fontVariantNumeric: 'tabular-nums',
  border: '1px solid',
  borderRadius: '2px',
  bgcolor: TOKENS.surface0,
  '&:focus': { outline: 'none', borderColor: TOKENS.brandBlue },
  '&:disabled': {
    bgcolor: TOKENS.surface50,
    color: 'text.disabled',
    cursor: 'not-allowed',
  },
} as const

/** Mid-week reschedule panel — a tab in the Factory schedule's bottom output
 * strip (alongside Crew & capacity / Resource usage / etc.), not a popup, so
 * it stays visible while the rest of the schedule is still on screen. Laid
 * out to use the full width of that panel: batch checklist on the left,
 * cutoff + downtime controls in a fixed sidebar on the right. */
export const MidWeekRescheduleTab = ({
  schedulerId,
  iteration,
  outputRows,
  downtimeRows,
  notProducedIds,
  onToggleNotProduced,
  onSaveDowntimeAsync,
  requirementRows = [],
  packagingRoutes = [],
  onSaveRequirementsAsync,
  stillToMake: stillToMakeProp,
  setStillToMake: setStillToMakeProp,
  dontStartBefore: dontStartBeforeProp,
  setDontStartBefore: setDontStartBeforeProp,
  finishBefore: finishBeforeProp,
  setFinishBefore: setFinishBeforeProp,
  weekBaseHour,
  runScheduler,
  isRunning,
}: MidWeekRescheduleTabProps) => {
  // ─── Cutoff: day + time-of-day, not just whole-day boundaries ────────────
  const [cutoffDay, setCutoffDay] = useState(2) // default Wed
  const [cutoffTime, setCutoffTime] = useState('09:00')
  // Least disruption: among the fastest schedules, publish the one whose
  // re-planned products start closest to the original. Off by default, so a
  // reschedule picks exactly as it did before the option existed.
  const [preferClosest, setPreferClosest] = useState(false)
  // Absolute hour — comparable against output rows' kitchen_start_time /
  // line_start_time (also absolute), and what actually gets sent as
  // fix_before_hour. weekBaseHour is 0 for a single-week schedule.
  const cutoffHour = useMemo(() => {
    const { hh, mm } = parseTimeStr(cutoffTime)
    return weekBaseHour + dayTimeToHour(cutoffDay, hh, mm)
  }, [cutoffDay, cutoffTime, weekBaseHour])
  // Display-only: subtract the week offset back out so labels read "Wed
  // 09:00" for the week on screen, not a wrapped/garbage day index.
  const cutoffLabel = hourToLabel(cutoffHour - weekBaseHour)

  // ─── The week's product table ─────────────────────────────────────────────
  //
  // One row per RUN for anything the schedule places (`lineProductKey` =
  // product + line + start hour) — that is the unit a machine fault stops
  // mid-run, and the unit the planner ticks. Plus one row per PRODUCT for
  // everything the requirements carry that has no run, which is the grain the
  // figures are stored at anyway.
  //
  // Mixing the two grains is deliberate. Collapsing the runs to product grain
  // is what broke the figures before (a product with two runs showed the same
  // total on both and neither came to Original - Made), and lifting the
  // requirements to run grain is impossible — they have no run.
  const requirementByCode = useMemo(() => {
    const out = new Map<string, ProductionRequirementRowType>()
    for (const r of requirementRows) {
      const code = String(r.productCode ?? '')
      if (code && !out.has(code)) out.set(code, r)
    }
    return out
  }, [requirementRows])

  /** Products some packing route can actually make. The engine drops a
   *  requirement whose product has none ("has demand but no packing route -
   *  excluded"), so a figure typed against one would silently do nothing.
   *  Such a row is still listed — hiding it invites the planner to conclude
   *  the product does not exist — but its quantity cell is disabled and says
   *  why. This is the check the old add-product dropdown applied by omission. */
  const routedProducts = useMemo(() => {
    const out = new Set<string>()
    for (const route of packagingRoutes) {
      const code = String(route.productCode ?? '')
      if (code) out.add(code)
    }
    return out
  }, [packagingRoutes])

  const productRows = useMemo((): ChecklistRow[] => {
    const byKey = new Map<string, ChecklistRow>()
    for (const row of outputRows) {
      const key = lineProductKey(row)
      const start = row.line_start_time ?? 0
      if (!byKey.has(key)) {
        byKey.set(key, {
          key,
          // Fixed per key: the start hour is part of the key, so a run cannot
          // change side of the cut-off without becoming a different row.
          kind: start < cutoffHour ? 'ran' : 'scheduled',
          productCode: row.productCode,
          productName: String(
            requirementByCode.get(row.productCode)?.productName ?? ''
          ),
          line: row.line,
          kitchen: row.kitchen,
          baseMaterial: row.base_material || row.productCode,
          startHour: start,
          quantity: 0,
          made: 0,
        })
      }
      const agg = byKey.get(key)!
      agg.quantity += row.quantity ?? 0
      // Naturally 0 for a `scheduled` run — the pro-rata fraction clamps at
      // zero once the run starts at or after the cut-off.
      agg.made += madeByCutoff(
        start,
        row.line_end_time ?? 0,
        row.quantity ?? 0,
        cutoffHour
      )
    }
    const rows = Array.from(byKey.values())
    const scheduled = new Set(rows.map((r) => r.productCode))
    for (const req of requirementRows) {
      const code = String(req.productCode ?? '')
      if (!code || scheduled.has(code)) continue
      const demand = demandOf(req)
      rows.push({
        key: `req:${code}`,
        kind: demand > 0 ? 'demand' : 'available',
        productCode: code,
        productName: String(req.productName ?? ''),
        line: '',
        kitchen: '',
        baseMaterial: '',
        startHour: null,
        quantity: demand,
        made: 0,
      })
    }
    return rows.sort(compareRows)
  }, [outputRows, cutoffHour, requirementRows, requirementByCode])

  const lockedProducts = useMemo(
    () => productRows.filter((p) => p.kind === 'ran'),
    [productRows]
  )
  // Runs after the cut-off. Requirement-derived rows are excluded on purpose:
  // the metric counts work the optimizer will move, and a product with no run
  // has nothing to move.
  const flexibleCount = useMemo(
    () => productRows.filter((p) => p.kind === 'scheduled').length,
    [productRows]
  )

  // ─── Search ───────────────────────────────────────────────────────────────
  // The table is ~350 rows on a real schedule and most of it is the available
  // tail, so reaching one product by eye is not realistic. Matches code, name,
  // line or kitchen — the four things a planner has in hand.
  const [search, setSearch] = useState('')
  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return productRows
    return productRows.filter(
      (r) =>
        r.productCode.toLowerCase().includes(q) ||
        r.productName.toLowerCase().includes(q) ||
        r.line.toLowerCase().includes(q) ||
        r.kitchen.toLowerCase().includes(q)
    )
  }, [productRows, search])

  // Bidirectional kitchen<->line expansion (shared with ScheduleControlPanel
  // and the Gantt) — a product ticked here also frees its kitchen batch once
  // every one of that batch's products is ticked, and vice versa.
  const expandedNotProduced = useMemo(
    () => expandNotProducedKeys(notProducedIds, outputRows),
    [notProducedIds, outputRows]
  )
  const notProducedCount = lockedProducts.filter((p) =>
    expandedNotProduced.has(p.key)
  ).length
  const toReOptimiseCount = flexibleCount + notProducedCount

  // ─── Ad-hoc downtime / machine fault ──────────────────────────────────────
  const [downtimeOpen, setDowntimeOpen] = useState(false)
  const [pendingDowntime, setPendingDowntime] = useState<PendingDowntime[]>([])
  const [newResource, setNewResource] = useState('')
  const [startDay, setStartDay] = useState(cutoffDay)
  const [startTime, setStartTime] = useState(cutoffTime)
  const [endDay, setEndDay] = useState(cutoffDay)
  const [endTime, setEndTime] = useState('12:00')

  const resourceOptions = useMemo(() => {
    const set = new Set<string>()
    downtimeRows.forEach((r) => set.add(r.resource))
    outputRows.forEach((r) => {
      if (r.kitchen) set.add(r.kitchen)
      if (r.line) set.add(r.line)
    })
    return Array.from(set).sort()
  }, [downtimeRows, outputRows])

  const handleAddDowntime = () => {
    if (!newResource) return
    const startParsed = parseTimeStr(startTime)
    const endParsed = parseTimeStr(endTime)
    // Widened to whole hours: the engine reads downtime as whole hours and
    // TRUNCATES both ends ("(57.5,59.5)" becomes 57-59), which would end a
    // 09:30-11:30 fault at 11:00 and let production run through the last half
    // hour of it. Rounding outward never lets a fault be shorter than typed.
    const s = Math.floor(
      dayTimeToHour(startDay, startParsed.hh, startParsed.mm)
    )
    const e = Math.ceil(dayTimeToHour(endDay, endParsed.hh, endParsed.mm))
    if (e <= s) return
    const existing = downtimeRows.find((r) => r.resource === newResource)
    // A resource with no row yet takes its type from the schedule. Defaulting
    // to 'line' filed a kitchen fault as LINE downtime, which the engine keys
    // on the kitchen's id in the line map, so the kitchen never stopped.
    const isKitchen = outputRows.some((r) => r.kitchen === newResource)
    setPendingDowntime((prev) => [
      ...prev,
      {
        resource: newResource,
        resourceType:
          existing?.resourceType ?? (isKitchen ? 'kitchen' : 'line'),
        startHour: s,
        endHour: e,
      },
    ])
    setNewResource('')
  }

  const removePendingDowntime = (idx: number) =>
    setPendingDowntime((prev) => prev.filter((_, i) => i !== idx))

  // ─── Run config seeding (preserve last-used GA/weight settings) ──────────
  const { data: scheduleSettingsSnapshot, isFetched: scheduleSettingsFetched } =
    useScheduleSettings(schedulerId, iteration)
  const { data: configSettings } = useConfigurationSettings()
  const seedSettings = scheduleSettingsSnapshot?.data ?? configSettings ?? null

  // ─── Still to make ────────────────────────────────────────────────────────
  //
  // Keyed by the checklist's per-RUN key, because that is the grain the
  // planner reads: a row shows one run's Original and Made, so the remainder
  // beside them has to be that run's too. Keying this by product code instead
  // — while the rows stayed per run — is what made the figure unreadable: a
  // product running twice showed the same total on both rows, and neither row
  // came to Original − Made.
  //
  // The WRITE is still per product, because `quantityStillToMake` is a column
  // on the production requirements table with one row per product. So the runs
  // are totalled at save time (`stillToMakeByProduct`). Display grain and
  // storage grain differ deliberately; collapsing them into one is the thing
  // that went wrong.
  const [localStillToMake, setLocalStillToMake] = useState<
    Record<string, string>
  >({})
  const stillToMake = stillToMakeProp ?? localStillToMake
  const setStillToMake = setStillToMakeProp ?? setLocalStillToMake

  /** What a run has produced by the cut-off, counted the way the ENGINE will
   *  credit it: zero for a run ticked "not produced", because that row becomes
   *  `RowDisposition::Replan` and `credited()` returns 0 (see the demand
   *  adjustments in `preprocess.rs`). Showing the pro-rata figure on a ticked
   *  row left the row reading Original − Made ≠ Still to make, and understated
   *  the remainder by exactly what the Made cell claimed. */
  const effectiveMade = (p: ChecklistRow): number =>
    expandedNotProduced.has(p.key) ? 0 : p.made

  /** Default remainder for ONE row.
   *
   *  A run, or a demanded product with no run, owes whatever it has not made
   *  by the cut-off. An `available` product owes NOTHING until the planner
   *  says otherwise — it is not in the week, and defaulting it to anything
   *  else would sweep the whole catalogue into the week's demand. Recomputed
   *  as the cut-off moves and as ticks change, which is why edits are held
   *  separately and only override where present. */
  const defaultStillToMake = (p: ChecklistRow): number =>
    p.kind === 'available' ? 0 : Math.max(0, p.quantity - effectiveMade(p))

  /** What the input box shows: the planner's raw text where they have typed
   *  something, otherwise the row's default. Raw rather than parsed, so a
   *  half-typed entry is not rewritten under the cursor. */
  const stillToMakeValue = (p: ChecklistRow): string =>
    stillToMake[p.key] ?? String(Math.round(defaultStillToMake(p)))

  /** One typed-or-default remainder per row. An empty box is a deliberate
   *  zero ("make no more of this"), the same reading the field has always
   *  had; an unparseable or negative entry falls back to the row's default
   *  rather than dropping that row out of its product's total silently. */
  const runStillToMake = (p: ChecklistRow): number => {
    const typed = stillToMake[p.key]
    if (typed === undefined) return defaultStillToMake(p)
    if (typed === '') return 0
    const value = Number(typed)
    return Number.isFinite(value) && value >= 0 ? value : defaultStillToMake(p)
  }

  /** The per-row figures totalled into the single number each product stores.
   *
   *  EVERY row of the product counts, not just the ones the search leaves on
   *  screen — the total is built from `productRows`, never from the filtered
   *  list, so typing in the search box can never change what gets saved.
   *
   *  A product with no run contributes its one requirement-derived row, which
   *  is how an available product's typed figure reaches the save at all. */
  const stillToMakeByProduct = useMemo(() => {
    const out: Record<string, number> = {}
    for (const p of productRows) {
      out[p.productCode] = (out[p.productCode] ?? 0) + runStillToMake(p)
    }
    return out
  }, [productRows, stillToMake, expandedNotProduced])

  /** Products with at least one hand-edited run. Only these are written: an
   *  untouched product keeps no `quantityStillToMake`, which leaves the
   *  engine's own estimate in charge rather than freezing a computed default
   *  into a planner-stated figure. */
  const editedProducts = useMemo(
    () =>
      Array.from(
        new Set(
          productRows
            .filter((p) => stillToMake[p.key] !== undefined)
            .map((p) => p.productCode)
        )
      ),
    [productRows, stillToMake]
  )

  // ─── Timing windows ───────────────────────────────────────────────────────
  //
  // `dontStartBefore` and `finishBefore` are existing per-week columns on the
  // production requirements, read by preprocess into `product_dont_start_before`
  // and `product_finish_before`. Editing them here writes the same columns the
  // Factory layout screen already writes — this is a second door onto one
  // field, not a new contract.
  //
  // Both are held per PRODUCT, because that is what the column is. A product
  // with two runs therefore shows the same value on both of its rows and
  // editing either moves both; that is honest about the underlying field,
  // where a blank on the second row would not be.
  //
  // `finishBefore` stays SOFT: it is written without `finishBeforeHard`, so it
  // is a late-penalty target the optimizer weighs, not a guarantee it will
  // meet. Same behaviour as the Factory layout screen, where the hard flag can
  // be set if a product needs one.
  const [localDontStartBefore, setLocalDontStartBefore] = useState<
    Record<string, string>
  >({})
  const dontStartBefore = dontStartBeforeProp ?? localDontStartBefore
  const setDontStartBefore = setDontStartBeforeProp ?? setLocalDontStartBefore
  const [localFinishBefore, setLocalFinishBefore] = useState<
    Record<string, string>
  >({})
  const finishBefore = finishBeforeProp ?? localFinishBefore
  const setFinishBefore = setFinishBeforeProp ?? setLocalFinishBefore

  /** What a timing box shows: the planner's raw text where they have typed
   *  something, otherwise whatever the requirement row already stores. */
  const timingValue = (
    edits: Record<string, string>,
    code: string,
    stored: number | null | undefined
  ): string =>
    edits[code] ??
    (stored === null || stored === undefined ? '' : String(stored))

  /** Cleared box means "no constraint" (null), which is how the column reads
   *  when absent. A negative or unparseable entry is dropped rather than
   *  written — an hour before the horizon starts constrains nothing and would
   *  only look like it had been accepted. */
  const parseTiming = (raw: string): number | null | undefined => {
    if (raw.trim() === '') return null
    const value = Number(raw)
    if (!Number.isFinite(value) || value < 0) return undefined
    return Math.round(value)
  }

  /** Hours are what the column stores, and what the Factory layout screen
   *  shows, so the input takes hours — but an hour number is not something a
   *  planner can read as a time, so the tooltip spells it out. Offset by
   *  `weekBaseHour` exactly as the Start column is: for a consolidated run
   *  these values share the output rows' absolute horizon frame, not the
   *  week-relative one downtime uses. */
  const timingTitle = (raw: string, blank: string): string => {
    const value = Number(raw)
    return raw.trim() !== '' && Number.isFinite(value)
      ? `Hour ${value} — ${hourToLabel(value - weekBaseHour)}`
      : blank
  }

  const editedTimingProducts = useMemo(
    () =>
      Array.from(
        new Set([...Object.keys(dontStartBefore), ...Object.keys(finishBefore)])
      ),
    [dontStartBefore, finishBefore]
  )

  // ─── Adding a product is now an edit, not an insert ───────────────────────
  //
  // The separate "add a product" panel has gone. It offered every product with
  // a packing route MINUS the ones this week already demands — and on real
  // data that set is empty: the requirements table carries a row per product
  // (350 rows on a tracked fixture, 324 of them at quantity 0), so a product
  // is out of the week because its row says 0, not because it has no row.
  //
  // So the table lists those rows instead and the figure is typed straight
  // into Still to make. D3's decision is unchanged and still holds — the value
  // is persisted to the week's `InputProductRequirementsDelta`, so it survives
  // into every later ordinary run rather than being run-scoped.
  // ─── Sorting ──────────────────────────────────────────────────────────────
  //
  // Tri-state per column: ascending, descending, then back to the default
  // order. The third click matters more than it looks — the default is the
  // LADDER (runs in the order they happen, then unscheduled demand, then the
  // available tail), which is the only ordering that keeps the rows a planner
  // came to act on at the top. Sorting ascending by Original puts 324
  // zero-demand rows above the week, and being able to get straight back is
  // what makes that safe to do.
  const [sort, setSort] = useState<{
    key: SortKey
    dir: 'asc' | 'desc'
  } | null>(null)

  const cycleSort = (key: SortKey) =>
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' }
      if (prev.dir === 'asc') return { key, dir: 'desc' }
      return null
    })

  /** The value a column sorts on — the same thing the cell displays, so the
   *  order always matches what is on screen. `Made` and `Still to make` are
   *  the computed/typed figures, not the raw stored ones, for that reason. */
  const sortValue = (r: ChecklistRow, key: SortKey): SortValue => {
    const req = requirementByCode.get(r.productCode)
    switch (key) {
      case 'ticked':
        return expandedNotProduced.has(r.key) ? 0 : 1
      case 'product':
        return r.productCode
      case 'line':
        return r.line || null
      case 'kitchen':
        return r.startHour !== null ? `${r.kitchen} · ${r.baseMaterial}` : null
      case 'start':
        return r.startHour
      case 'original':
        return r.quantity
      case 'made':
        return effectiveMade(r)
      case 'still':
        return runStillToMake(r)
      case 'dsb': {
        const raw = timingValue(
          dontStartBefore,
          r.productCode,
          req?.dontStartBefore
        )
        return raw.trim() === '' ? null : Number(raw)
      }
      case 'fb': {
        const raw = timingValue(finishBefore, r.productCode, req?.finishBefore)
        return raw.trim() === '' ? null : Number(raw)
      }
    }
  }

  const sortedRows = useMemo(() => {
    if (!sort) return visibleRows
    // Decorate-sort-undecorate: sortValue is not free (a map lookup and, for
    // the timing columns, a parse) and Array.sort would call it O(n log n)
    // times per row on a ~350-row table.
    return visibleRows
      .map((r) => ({ r, v: sortValue(r, sort.key) }))
      .sort(
        (x, y) => compareSortValues(x.v, y.v, sort.dir) || compareRows(x.r, y.r)
      )
      .map((x) => x.r)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    visibleRows,
    sort,
    stillToMake,
    expandedNotProduced,
    dontStartBefore,
    finishBefore,
    requirementByCode,
  ])

  // Cap the available tail only. A run or a demanded product is never hidden,
  // however narrow the search or whatever the sort — those rows ARE the week,
  // and silently dropping one would be a far worse failure than a long list.
  // Applied AFTER the sort, so the rows kept are the top ones by whatever the
  // planner is looking at rather than a fixed slice of the catalogue.
  const { shownRows, hiddenAvailable } = useMemo(() => {
    const shown: ChecklistRow[] = []
    let available = 0
    let hidden = 0
    for (const r of sortedRows) {
      if (r.kind === 'available') {
        available += 1
        if (available > AVAILABLE_ROW_CAP) {
          hidden += 1
          continue
        }
      }
      shown.push(r)
    }
    return { shownRows: shown, hiddenAvailable: hidden }
  }, [sortedRows])

  // Decides what is OFFERED only — the run is refused server-side by
  // security.auth.requires('run') regardless of what renders here.
  const { canRun } = usePermissions()

  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleLockAndRerun = async () => {
    if (isSubmitting || isRunning) return
    setIsSubmitting(true)
    try {
      if (pendingDowntime.length > 0) {
        const changed: Record<number, WorkstationDownTimeRowType> = {}
        let nextNewId = -1
        for (const pending of pendingDowntime) {
          const existing = downtimeRows.find(
            (r) => r.resource === pending.resource
          )
          if (existing) {
            const windows: Window[] = [
              ...parsePeriods(existing.downTime),
              [pending.startHour, pending.endHour],
            ]
            changed[existing.id] = {
              ...existing,
              downTime: joinPeriods(windows),
            }
          } else {
            const id = nextNewId--
            changed[id] = {
              id,
              resource: pending.resource,
              resourceType: pending.resourceType,
              downTime: joinPeriods([[pending.startHour, pending.endHour]]),
              site_location: '',
            }
          }
        }
        // Await so the run below can't start before this PUT lands —
        // scheduler_api reads workstation-downtime fresh from storage at
        // run time, so an un-awaited save here would race the run.
        try {
          await onSaveDowntimeAsync(changed)
        } catch {
          toast.error(
            'Failed to save the downtime change — reschedule not started.'
          )
          return
        }
        setPendingDowntime([])
      }

      // Write the remainders BEFORE the run, and await them — the run reads
      // the production requirements fresh from storage, exactly as it does
      // the downtime table above.
      //
      // Only edited products are written. An untouched product keeps no
      // `quantityStillToMake` at all, which is what leaves the engine's own
      // pro-rata estimate in charge of it; writing the computed default back
      // would silently convert every estimate into a planner-stated figure
      // and freeze it against a later change of cut-off.
      if (
        (editedProducts.length > 0 || editedTimingProducts.length > 0) &&
        onSaveRequirementsAsync
      ) {
        const changed: Record<number, ProductionRequirementRowType> = {}
        // One row may be touched by both loops below — a product can have its
        // remainder AND its timing edited — so each takes the row already
        // being written rather than a fresh copy of the stored one, which
        // would drop whichever edit ran first.
        const touch = (req: ProductionRequirementRowType) => {
          if (!changed[req.id]) changed[req.id] = { ...req }
          return changed[req.id]
        }

        // The stored figure is the product's TOTAL across every row — the
        // edited ones at their typed value, the rest at their default, and
        // including runs after the cut-off. The engine reads
        // `quantityStillToMake` as the whole of that product's remaining
        // demand for the week (it suppresses the engine's own pro-rata
        // estimate rather than stacking with it), so writing one row's
        // remainder there would erase the others.
        //
        // WHICH COLUMN it lands in depends on whether the product already has
        // demand, and the engine leaves no choice about that. `preprocess.rs`
        // drops a requirement row before it ever reads `quantityStillToMake`:
        //
        //     if original_quantity.map_or(true, |q| q <= 0.0) { return None; }
        //
        // so on a zero-quantity row a remainder would be written into a field
        // nothing goes on to read — the product would simply not appear, with
        // no warning. There the figure has to become `quantity`, which is
        // exactly what the old add-product path wrote and for the same
        // reason: a product being brought into the week has had nothing made,
        // so there is no remainder to state, only a demand. The run's
        // `min_start_hour` still stops it being backdated before the cut-off.
        for (const productCode of editedProducts) {
          const req = requirementByCode.get(productCode)
          if (!req) continue
          const value = stillToMakeByProduct[productCode]
          if (value === undefined || !Number.isFinite(value) || value < 0) {
            continue
          }
          if (demandOf(req) > 0) {
            touch(req).quantityStillToMake = value
          } else {
            touch(req).quantity = String(Math.round(value))
          }
        }

        // Timing windows are plain per-product columns — nothing to total, and
        // no dependence on what was made. `finishBeforeHard` is deliberately
        // left alone: this panel writes a soft target and leaves the hard flag
        // to the Factory layout screen that already owns it.
        for (const productCode of editedTimingProducts) {
          const req = requirementByCode.get(productCode)
          if (!req) continue
          const row = touch(req)
          if (dontStartBefore[productCode] !== undefined) {
            const parsed = parseTiming(dontStartBefore[productCode])
            if (parsed !== undefined) row.dontStartBefore = parsed
          }
          if (finishBefore[productCode] !== undefined) {
            const parsed = parseTiming(finishBefore[productCode])
            if (parsed !== undefined) row.finishBefore = parsed
          }
        }

        if (Object.keys(changed).length > 0) {
          try {
            await onSaveRequirementsAsync(changed)
          } catch {
            toast.error(
              'Failed to save the quantities — reschedule not started.'
            )
            return
          }
        }
      }

      const baseConfig = seedSettings ? settingsToRunConfig(seedSettings) : {}
      runScheduler({
        schedulerId,
        iteration,
        config: {
          ...baseConfig,
          fix_before_hour: cutoffHour,
          not_produced_keys: Array.from(
            expandNotProducedKeys(notProducedIds, outputRows)
          ),
          // Without this, freeing up every pre-cutoff product (e.g.
          // ticking everything after a total stoppage) removes every
          // downtime block fix_before_hour would otherwise create, leaving
          // the solver free to backdate fresh production before the
          // cutoff too. Same hour as fix_before_hour — nothing new should
          // start earlier than the point you're rescheduling from.
          min_start_hour: cutoffHour,
          midweek_prefer_closest: preferClosest,
        },
      })

      // The edits are now in the production requirements, so the local
      // overrides have done their job. Clearing them is not tidying: these
      // maps outlive the panel, and left in place they would shadow the very
      // figures the save just stored, while a remainder keyed to a run this
      // re-run is about to replace would carry into a schedule it was never
      // typed against. Only reached on the success path — an early `return`
      // above leaves the edits alone so they can be retried.
      setStillToMake({})
      setDontStartBefore({})
      setFinishBefore({})
    } finally {
      setIsSubmitting(false)
    }
  }

  const hasData = outputRows.length > 0

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        bgcolor: TOKENS.surface0,
      }}
    >
      <PanelHead
        icon={<EventRepeatIcon sx={{ fontSize: 19 }} />}
        title={`Mid-week reschedule — Iteration ${iteration}`}
        sub="Lock what's already run, free up what didn't happen, adjust what's left to make, and re-optimise the rest. Always builds from the original schedule, independent of any prior reschedule."
        metrics={
          hasData
            ? [
                { label: 'Cutoff', value: cutoffLabel },
                {
                  label: 'Locked',
                  value: lockedProducts.length - notProducedCount,
                },
                {
                  label: 'Not produced',
                  value: notProducedCount,
                  tone: notProducedCount ? TOKENS.stateRunning : TOKENS.ink400,
                },
                {
                  label: 'To re-optimise',
                  value: toReOptimiseCount,
                  tone: TOKENS.stateComplete,
                },
              ]
            : undefined
        }
      />

      {!hasData ? (
        <Box sx={{ p: 3 }}>
          <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
            No schedule output yet — run the scheduler first.
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            minHeight: 0,
            px: '18px',
            py: '16px',
            display: 'flex',
            gap: '20px',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
          }}
        >
          {/* ── Left: the week's products ── */}
          <Box sx={{ flex: 1, minWidth: 420 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                mb: 0.5,
                flexWrap: 'wrap',
              }}
            >
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700 }}>
                The week&apos;s products
              </Typography>
              <TextField
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search code, name, line or kitchen…"
                sx={{
                  width: 280,
                  '& .MuiInputBase-input': { fontSize: '0.72rem', py: '6px' },
                }}
              />
            </Box>
            <Typography
              sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 1.25 }}
            >
              Tick a run that has NOT actually been produced (e.g. a machine
              fault stopped it) — it is freed up and re-optimised along with
              everything after your cutoff. Every product the week can make is
              listed: give one a{' '}
              <Box component="span" sx={{ fontWeight: 600 }}>
                Still to make
              </Box>{' '}
              figure to bring it into the week, or set one to zero to take it
              out.
            </Typography>
            {shownRows.length === 0 ? (
              <Typography
                sx={{ fontSize: '0.75rem', color: 'text.disabled', mb: 1 }}
              >
                {productRows.length === 0
                  ? 'No products on this schedule yet.'
                  : `No product matches “${search.trim()}”.`}
              </Typography>
            ) : (
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ overflowX: 'auto' }}>
                  <Box sx={{ minWidth: CHECKLIST_MIN_W }}>
                    {/* Column headers */}
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: CHECKLIST_ROW_COLS,
                        gap: 1,
                        px: 1,
                        py: 0.5,
                        bgcolor: TOKENS.surface50,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      {CHECKLIST_COLUMNS.map((col) => {
                        const active = sort?.key === col.key
                        return (
                          <Typography
                            key={col.key}
                            component="div"
                            onClick={() => cycleSort(col.key)}
                            title={`Sort by ${col.label}. Click again to reverse, a third time to restore the default order.`}
                            sx={{
                              fontSize: '0.6rem',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '0.04em',
                              color: active
                                ? TOKENS.brandBlue
                                : 'text.secondary',
                              cursor: 'pointer',
                              userSelect: 'none',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.25,
                              minWidth: 0,
                              '&:hover': { color: TOKENS.brandBlue },
                            }}
                          >
                            <Box component="span" sx={{ ...ELLIPSIS }}>
                              {col.label}
                            </Box>
                            {/* Reserved whether or not this column is the
                                sorted one, so the header text does not shift
                                sideways as the arrow appears. */}
                            <Box
                              component="span"
                              sx={{
                                flex: '0 0 auto',
                                width: 8,
                                opacity: active ? 1 : 0,
                              }}
                            >
                              {sort?.dir === 'desc' ? '▼' : '▲'}
                            </Box>
                          </Typography>
                        )
                      })}
                    </Box>
                    <Box sx={{ maxHeight: 460, overflowY: 'auto' }}>
                      {shownRows.map((product) => {
                        // Only a run that started before the cut-off can be
                        // ticked — the tick means "this did not actually
                        // happen", which says nothing about work not yet due
                        // to start.
                        const isRun = product.kind === 'ran'
                        const checked =
                          isRun && expandedNotProduced.has(product.key)
                        const req = requirementByCode.get(product.productCode)
                        const hasRoute = routedProducts.has(product.productCode)
                        // Both reasons a figure typed here would go nowhere:
                        // the engine excludes a product with no packing route,
                        // and a product with no requirement row has no column
                        // to be written to. Disabled and explained, rather
                        // than accepted and silently dropped at save time.
                        const editable = hasRoute && req !== undefined
                        const blockedWhy = !hasRoute
                          ? 'No packing route — the engine excludes this product, so a figure here would do nothing.'
                          : 'No production-requirement row for this product, so there is nowhere to save a figure.'
                        return (
                          <Box
                            key={product.key}
                            onClick={
                              isRun
                                ? () => onToggleNotProduced(product.key)
                                : undefined
                            }
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: CHECKLIST_ROW_COLS,
                              gap: 1,
                              alignItems: 'center',
                              px: 1,
                              py: 0.6,
                              cursor: isRun ? 'pointer' : 'default',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              bgcolor: checked
                                ? 'rgba(199,127,42,0.08)'
                                : 'transparent',
                              opacity: hasRoute ? 1 : 0.55,
                              '&:hover': { bgcolor: 'action.hover' },
                              '&:last-child': { borderBottom: 'none' },
                            }}
                          >
                            {isRun ? (
                              <Checkbox
                                size="small"
                                checked={checked}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() =>
                                  onToggleNotProduced(product.key)
                                }
                                sx={{ p: 0.25 }}
                              />
                            ) : (
                              <Box />
                            )}
                            <Box
                              sx={{ ...ELLIPSIS }}
                              title={editable ? undefined : blockedWhy}
                            >
                              <Typography
                                component="span"
                                sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                              >
                                {product.productCode}
                              </Typography>
                              {product.productName && (
                                <Typography
                                  component="span"
                                  sx={{
                                    fontSize: '0.66rem',
                                    color: 'text.secondary',
                                    ml: 0.75,
                                  }}
                                >
                                  {product.productName}
                                </Typography>
                              )}
                            </Box>
                            <Typography sx={CELL}>
                              {product.line || '—'}
                            </Typography>
                            <Typography sx={CELL}>
                              {product.startHour !== null
                                ? `${product.kitchen} · ${product.baseMaterial}`
                                : '—'}
                            </Typography>
                            {/* A product with no run has no start to show, so
                                the cell says WHY instead — the difference
                                between "the week owes this but has not placed
                                it" and "this is merely available". */}
                            <Typography sx={CELL}>
                              {product.startHour !== null
                                ? hourToLabel(product.startHour - weekBaseHour)
                                : product.kind === 'demand'
                                  ? 'Not scheduled'
                                  : 'No demand'}
                            </Typography>
                            <Typography sx={NUM_CELL}>
                              {Math.round(product.quantity).toLocaleString()}
                            </Typography>
                            {/* Computed pro-rata across the run, not typed —
                                the engine works out the same figure, so a
                                planner who changes nothing still gets the
                                right answer. Drops to zero when the run is
                                ticked "not produced", because that is what the
                                engine then credits, and is zero for anything
                                that had not started by the cut-off. */}
                            <Typography
                              sx={NUM_CELL}
                              title={
                                checked
                                  ? 'Ticked "not produced" — nothing is credited for this run'
                                  : 'Produced by the cut-off, pro-rata across the run'
                              }
                            >
                              {Math.round(
                                effectiveMade(product)
                              ).toLocaleString()}
                            </Typography>
                            {/* Zero is a real answer — "make no more of this"
                                — and is what the tick expresses as a quantity.
                                On a product with no demand the same box is how
                                one is brought into the week. */}
                            <Box
                              component="input"
                              type="number"
                              min={0}
                              disabled={!editable}
                              title={editable ? undefined : blockedWhy}
                              value={stillToMakeValue(product)}
                              onClick={(e: React.MouseEvent) =>
                                e.stopPropagation()
                              }
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                setStillToMake((prev) => ({
                                  ...prev,
                                  [product.key]: e.target.value,
                                }))
                                // A run that finished before the cut-off has
                                // nothing left to make, so a figure typed
                                // against it means "this did not happen" —
                                // tick it, or the engine keeps the run as made
                                // AND plans the figure again, and the product
                                // shows twice (31690490844). The tick is
                                // visible and can be undone.
                                const finished =
                                  isRun &&
                                  product.made >= product.quantity - 1e-6
                                if (
                                  finished &&
                                  !checked &&
                                  Number(e.target.value) > 0
                                ) {
                                  onToggleNotProduced(product.key)
                                }
                              }}
                              sx={{
                                ...NUM_INPUT,
                                borderColor:
                                  stillToMake[product.key] !== undefined
                                    ? TOKENS.brandBlue
                                    : TOKENS.line300,
                              }}
                            />
                            {/* Both timing boxes are per PRODUCT, not per run:
                                they edit one column on the requirement row, so
                                a product with two runs shows the same value
                                twice and either box moves both. */}
                            <Box
                              component="input"
                              type="number"
                              min={0}
                              placeholder="—"
                              disabled={!editable}
                              title={
                                editable
                                  ? timingTitle(
                                      timingValue(
                                        dontStartBefore,
                                        product.productCode,
                                        req?.dontStartBefore
                                      ),
                                      'Earliest hour this product may start. Blank means no constraint. The cut-off already floors every product, so a value below it changes nothing.'
                                    )
                                  : blockedWhy
                              }
                              value={timingValue(
                                dontStartBefore,
                                product.productCode,
                                req?.dontStartBefore
                              )}
                              onClick={(e: React.MouseEvent) =>
                                e.stopPropagation()
                              }
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) =>
                                setDontStartBefore((prev) => ({
                                  ...prev,
                                  [product.productCode]: e.target.value,
                                }))
                              }
                              sx={{
                                ...NUM_INPUT,
                                borderColor:
                                  dontStartBefore[product.productCode] !==
                                  undefined
                                    ? TOKENS.brandBlue
                                    : TOKENS.line300,
                              }}
                            />
                            <Box
                              component="input"
                              type="number"
                              min={0}
                              placeholder="—"
                              disabled={!editable}
                              title={
                                editable
                                  ? timingTitle(
                                      timingValue(
                                        finishBefore,
                                        product.productCode,
                                        req?.finishBefore
                                      ),
                                      'Target hour to finish by. A soft target — the optimizer is penalised for missing it, not prevented.'
                                    )
                                  : blockedWhy
                              }
                              value={timingValue(
                                finishBefore,
                                product.productCode,
                                req?.finishBefore
                              )}
                              onClick={(e: React.MouseEvent) =>
                                e.stopPropagation()
                              }
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) =>
                                setFinishBefore((prev) => ({
                                  ...prev,
                                  [product.productCode]: e.target.value,
                                }))
                              }
                              sx={{
                                ...NUM_INPUT,
                                borderColor:
                                  finishBefore[product.productCode] !==
                                  undefined
                                    ? TOKENS.brandBlue
                                    : TOKENS.line300,
                              }}
                            />
                          </Box>
                        )
                      })}
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
            {hiddenAvailable > 0 && (
              <Typography
                sx={{ fontSize: '0.66rem', color: 'text.secondary', mt: 0.75 }}
              >
                {hiddenAvailable.toLocaleString()} further product
                {hiddenAvailable === 1 ? '' : 's'} with no demand not shown —
                search to narrow the list. Nothing scheduled or demanded is ever
                hidden.
              </Typography>
            )}
          </Box>

          {/* ── Right: fixed-width control sidebar ── */}
          <Box
            sx={{
              width: SIDEBAR_W,
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            {/* Cutoff picker */}
            <Box
              sx={{
                border: `1px solid ${TOKENS.line200}`,
                borderRadius: 1.5,
                p: 1.5,
                bgcolor: TOKENS.surface50,
              }}
            >
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, mb: 1 }}>
                Reschedule everything from
              </Typography>
              <Box
                sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.75 }}
              >
                <FormControl size="small" sx={{ minWidth: 96 }}>
                  <Select
                    value={cutoffDay}
                    onChange={(e) => setCutoffDay(Number(e.target.value))}
                  >
                    {DAY_LABELS.map((d, i) => (
                      <MenuItem key={d} value={i}>
                        {d}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  type="time"
                  size="small"
                  value={cutoffTime}
                  onChange={(e) => setCutoffTime(e.target.value)}
                  sx={{ flex: 1 }}
                />
              </Box>
              <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                = hour {(cutoffHour - weekBaseHour).toFixed(1)} of 168
                {weekBaseHour > 0 &&
                  ` (absolute hour ${cutoffHour.toFixed(1)})`}
              </Typography>
              {cutoffHour <= weekBaseHour && (
                <Typography
                  sx={{ fontSize: '0.65rem', color: '#ed6c02', mt: 0.5 }}
                >
                  This locks nothing — identical to a fresh run.
                </Typography>
              )}
            </Box>

            {/* Least disruption toggle */}
            <Box
              sx={{
                border: `1px solid ${TOKENS.line200}`,
                borderRadius: 1.5,
                p: 1.5,
                bgcolor: TOKENS.surface50,
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={preferClosest}
                    onChange={(e) => setPreferClosest(e.target.checked)}
                  />
                }
                label={
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700 }}>
                    Least disruption
                  </Typography>
                }
              />
              <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                {preferClosest
                  ? 'Of the fastest schedules, use the one that keeps each product starting closest to its original time.'
                  : 'Use the fastest schedule, however far it moves products from their original start times.'}
              </Typography>
            </Box>

            {/* Downtime / machine fault */}
            <Box
              sx={{
                border: `1px solid ${TOKENS.line200}`,
                borderRadius: 1.5,
                p: 1.5,
                bgcolor: TOKENS.surface50,
              }}
            >
              <Button
                size="small"
                startIcon={<BuildCircleIcon sx={{ fontSize: 16 }} />}
                onClick={() => {
                  // Start the fault at the cut-off as it is NOW. These were
                  // seeded once, at mount, so moving the cut-off left the
                  // fault defaulting to wherever it used to be.
                  if (!downtimeOpen) {
                    setStartDay(cutoffDay)
                    setStartTime(cutoffTime)
                    setEndDay(cutoffDay)
                  }
                  setDowntimeOpen((v) => !v)
                }}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.72rem',
                  p: 0,
                  mb: downtimeOpen ? 1.25 : 0,
                  justifyContent: 'flex-start',
                }}
              >
                {downtimeOpen
                  ? 'Declare a machine fault / downtime'
                  : '+ Declare a machine fault / downtime'}
              </Button>
              {downtimeOpen && (
                <>
                  <FormControl size="small" fullWidth sx={{ mb: 1 }}>
                    <Select
                      displayEmpty
                      value={newResource}
                      onChange={(e) => setNewResource(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Resource</em>
                      </MenuItem>
                      {resourceOptions.map((r) => (
                        <MenuItem key={r} value={r}>
                          {r}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 0.75,
                      alignItems: 'center',
                      mb: 0.75,
                    }}
                  >
                    <FormControl size="small" sx={{ minWidth: 76 }}>
                      <Select
                        value={startDay}
                        onChange={(e) => setStartDay(Number(e.target.value))}
                      >
                        {DAY_LABELS.map((d, i) => (
                          <MenuItem key={d} value={i}>
                            {d}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      type="time"
                      size="small"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      sx={{ flex: 1 }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.65rem',
                      color: 'text.secondary',
                      mb: 0.75,
                    }}
                  >
                    to
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 0.75,
                      alignItems: 'center',
                      mb: 1,
                    }}
                  >
                    <FormControl size="small" sx={{ minWidth: 76 }}>
                      <Select
                        value={endDay}
                        onChange={(e) => setEndDay(Number(e.target.value))}
                      >
                        {DAY_LABELS.map((d, i) => (
                          <MenuItem key={d} value={i}>
                            {d}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      type="time"
                      size="small"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      sx={{ flex: 1 }}
                    />
                    <Tooltip title="Add this downtime window">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={handleAddDowntime}
                        disabled={!newResource}
                      >
                        <AddIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {pendingDowntime.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {pendingDowntime.map((pd, i) => (
                        <Chip
                          key={i}
                          label={`${pd.resource}: ${hourToLabel(pd.startHour)} → ${hourToLabel(pd.endHour)}`}
                          size="small"
                          onDelete={() => removePendingDowntime(i)}
                          sx={{
                            fontSize: '0.62rem',
                            bgcolor: 'rgba(196,69,54,0.08)',
                            color: TOKENS.ganttDowntime,
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </>
              )}
            </Box>

            <Button
              variant="contained"
              disabled={
                isRunning || isSubmitting || !scheduleSettingsFetched || !canRun
              }
              title={
                canRun
                  ? undefined
                  : 'Your role does not allow running schedules.'
              }
              onClick={handleLockAndRerun}
              startIcon={
                isRunning || isSubmitting ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <PlayArrowIcon sx={{ fontSize: 16 }} />
                )
              }
              sx={{ textTransform: 'none', fontFamily: FONT_SANS }}
            >
              {!canRun
                ? 'View only'
                : isRunning
                  ? 'Re-planning…'
                  : isSubmitting
                    ? 'Saving downtime…'
                    : !scheduleSettingsFetched
                      ? 'Loading settings…'
                      : `Lock through ${cutoffLabel} & re-run`}
            </Button>
            <Typography
              sx={{ fontSize: '0.62rem', color: 'text.disabled', mt: -0.5 }}
            >
              Sends {expandedNotProduced.size} override key
              {expandedNotProduced.size === 1 ? '' : 's'} to the scheduler.
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  )
}
